<!-- NoteList.vue -->
<!-- PATCH: adicionados useNoteShare, ShareNotePopover e botão de compartilhamento -->
<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { useNoteStore } from "@/stores/useNoteStore";

import NoteCrudDropdown from "./NoteCrudDropdown.vue";
import DeleteNoteForm from "./DeleteNoteForm.vue";
import UserCardModal from "@/components/User/UserCardModal.vue";
import NoteCardModal from "./NoteCardModal.vue";
import TagCardModal from "@/components/Tag/TagCardModal.vue";
import AddTagForm from "@/components/Tag/AddTagForm.vue";
// >>> NOVO: modal de anexo (imagem/documento) linkado no campo "fonte" da nota
import NoteAttachmentModal from "./NoteAttachmentModal.vue";
// >>> NOVO
import ShareNotePopover from "./ShareNotePopover.vue";
import { useNoteShare } from "@/composables/useNoteShare";
// Links da nota: extração, previews (imagem, YouTube, documento, site) e anexos
import {
  linkifyBody,
  extractUrls,
  getNoteAttachments,
  hasAttachment,
} from "@/utils/noteLinks";
import { useLinkPreviews } from "@/composables/useLinkPreviews";
// >>> FLUXO 2: botão direto — "Ver trecho referenciado" (sem menu de contexto)
import { useMemberRestrictions } from "@/composables/useMemberRestrictions";
// Gate por nota (editar/excluir) compartilhado com o "marcar todas" do NoteSection.
import { useNoteManagePermission } from "@/composables/useNoteManagePermission";
import { toTitleCase } from "@/utils/stringUtils";
import { useTagStore } from "@/stores/useTagStore";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

import api from "@/services/api";

const props = defineProps({
  userId: { type: Number, required: true },
  textId: { type: Number, required: true },
  textCreatedByUserId: { type: Number, required: true },
  textUpdatedByUserId: { type: Number, required: true },
  activityCreatedByUserId: { type: Number, required: true },
  // ✅ PATCH: recebido da useNoteStore via componente pai
  isLoadingNotes: { type: Boolean, default: false },
});

// ✅ PATCH: permite repassar notificações de filhos para o componente pai
// >>> NOVO: "add-detached" abre o modal de nota avulsa no NoteSection
const emit = defineEmits(["notify", "add-detached"]);

const noteStore = useNoteStore();
const tagStore = useTagStore();

// >>> NOVO: composable de compartilhamento
const {
  isSharePopoverOpen,
  absoluteShareUrl,
  isGenerating,
  isCopied,
  isRevoking,
  shareError,
  shareNoteId,
  openSharePopover,
  generateLink, // ← adicionar esta linha
  closeSharePopover,
  copyLink,
  shareWhatsApp,
  shareEmail,
  shareNative,
  revokeLink,
} = useNoteShare();

// Lista efetivamente renderizada: a página do servidor, ou o recorte do trecho
// clicado no texto quando há foco. Tudo que fala "a nota que está na tela" lê
// daqui — inclusive o compartilhamento abaixo, já que uma nota em foco pode não
// estar na página carregada.
const displayedNotes = computed(() => noteStore.displayedNotes);

// Seguindo a leitura, a faixa muda de texto e o "Ver todas" muda de sentido: ele
// desliga o acompanhamento, senão a próxima rolagem refaria o recorte e o botão
// pareceria não ter funcionado.
const isFollowingReading = computed(
  () => noteStore.noteFocusMode === "reading"
);

function clearNoteFocusOrFollow() {
  if (isFollowingReading.value) noteStore.setFollowReading(false);
  else noteStore.clearNoteFocus();
}

// >>> NOVO: título da nota sendo compartilhada (para email/native)
const sharingNoteTitle = computed(() => {
  if (!shareNoteId.value) return "";
  const n = displayedNotes.value.find((n) => n.noteId === shareNoteId.value);
  return n?.title ?? "";
});

// >>> NOVO: verifica se o usuário logado é dono da nota sendo compartilhada
const isShareOwner = computed(() => {
  if (!shareNoteId.value) return false;
  const n = displayedNotes.value.find((n) => n.noteId === shareNoteId.value);
  return n?.createdByUserId === props.userId;
});

// Gate por nota centralizado (isSysUser/canEditNote/canManageNote) — mesma regra
// usada pelo "marcar todas" do NoteSection.
const { isSysUser, canEditNote, canManageNote, canFullyEditNote } =
  useNoteManagePermission(() => ({
    userId: props.userId,
    textCreatedByUserId: props.textCreatedByUserId,
    activityCreatedByUserId: props.activityCreatedByUserId,
  }));

// ── Edição no próprio card ───────────────────────────────────────────────────
// O lápis de cada campo NÃO abre um editor daquele campo: ele transforma o card
// inteiro em formulário e põe o cursor no campo escolhido. A diferença importa
// porque gravar é atômico — PUT /notes/{id} substitui a nota inteira (ver
// `updateNoteFields` na store). Editar campo a campo obrigaria a responder "e se
// o usuário sair no meio?" quatro vezes, e qualquer resposta (salvar sozinho,
// descartar calado) surpreende dentro de uma lista que rola.
//
// Só um card por vez: enquanto um está aberto, os lápis dos outros somem. Assim
// nenhum clique descarta rascunho alheio sem avisar.
const editingNoteId = ref(null);
const editDraft = ref(null);
const isSavingEdit = ref(false);
const editError = ref("");

// Rascunhos de cards fechados por clique fora, por noteId. Clicar fora não é
// desistir — é olhar outra coisa —, então o card volta a ser card sem salvar,
// mas o que estava escrito espera ali e volta inteiro no próximo lápis.
// "Cancelar" e o Esc, esses sim, jogam fora: são o gesto de desistir.
//
// Objeto simples, e não `ref`: nada aqui é renderizado, só lido ao reabrir.
// Vive enquanto a lista estiver montada — sair da tela leva os rascunhos junto.
const pendingDrafts = {};

// Tags disponíveis para escolher. Carregadas na primeira edição, e não na
// montagem: quem só lê notas não precisa da lista inteira de tags.
const allTags = ref([]);
const isLoadingTags = ref(false);

// Objeto simples, e não refs: `ref` dentro de `v-for` vira ARRAY no Vue 3, e só
// um card edita por vez — o array só atrapalharia. Também não precisa ser
// reativo: serve para chamar focus(), não para renderizar.
const editRefs = {};
function setEditRef(field) {
  return (el) => {
    editRefs[field] = el;
  };
}

function isEditing(note) {
  return editingNoteId.value === note.noteId;
}

// Lápis some nos cards dos outros enquanto um card está aberto — ver acima.
function canInlineEdit(note) {
  if (editingNoteId.value && editingNoteId.value !== note.noteId) return false;
  return canFullyEditNote(note);
}

async function loadTagOptions() {
  if (allTags.value.length || isLoadingTags.value) return;
  isLoadingTags.value = true;
  try {
    await tagStore.fetchTags();
    allTags.value = (tagStore.tags || []).map((tag) => ({
      label: tag.tagName,
      value: tag.tagId,
    }));
  } catch (error) {
    console.warn("Falha ao carregar as tags para edição:", error);
  } finally {
    isLoadingTags.value = false;
  }
}

// Tag nova: o seletor não cria sozinho, abre o MESMO formulário de cadastro do
// resto do sistema. Criar calada com valores padrão seria um segundo cadastro
// de tag, mais pobre e invisível — a tag nasceria sem cor nem descrição e
// ninguém saberia por quê. Aqui o usuário vê o que está criando, e a tag volta
// já aplicada à nota.
const showAddTagModal = ref(false);
const newTagName = ref("");

function createTagInline(rawName) {
  newTagName.value = toTitleCase(String(rawName ?? "").trim());
  showAddTagModal.value = true;
}

// Volta do formulário: entra na lista de opções E já na nota em edição, senão o
// usuário teria de procurá-la no seletor logo depois de tê-la criado.
function handleTagCreated({ tagId, tagName }) {
  const option = { label: tagName, value: tagId };
  if (!allTags.value.some((tag) => tag.value === tagId)) {
    allTags.value.push(option);
  }
  if (editDraft.value && !editDraft.value.tags.some((t) => t.value === tagId)) {
    editDraft.value.tags.push(option);
  }
  showAddTagModal.value = false;
  newTagName.value = "";
}

async function startInlineEdit(note, field) {
  if (!canFullyEditNote(note)) return;

  editingNoteId.value = note.noteId;
  editError.value = "";

  // Rascunho pendente vence a nota: se o card foi fechado por clique fora, o
  // que o usuário escreveu é mais recente que o que está gravado, e reconstruir
  // a partir da nota apagaria o trabalho dele em silêncio.
  //
  // Sem rascunho, o ponto de partida sai do CARD, e não de uma releitura: o que
  // se vê é o que se edita. Quem garante que nada mais da nota se perde é a
  // releitura feita na hora de GRAVAR, dentro de `updateNoteFields`.
  editDraft.value = pendingDrafts[note.noteId] ?? {
    title: note.title ?? "",
    body: note.body ?? "",
    source: note.source ?? "",
    tags: (note.tagNames ?? []).map((label, index) => ({
      label,
      value: note.tagIds?.[index] ?? null,
    })),
  };

  loadTagOptions();

  await nextTick();

  // As tags são a exceção: o formulário abre sem foco nenhum.
  //
  // Dar foco ao seletor abriria a lista de tags por cima do resto do
  // formulário, escondendo justamente os campos que o usuário talvez tenha
  // vindo conferir — e um menu que se abre sozinho pede para ser fechado antes
  // de qualquer outra coisa. Nos campos de texto o foco é ganho, porque lá ele
  // deixa o cursor pronto para digitar sem cobrir nada.
  if (field !== "tags") focusFieldEnd(editRefs[field]);

  // A rolagem vem DEPOIS do foco, e não antes.
  //
  // Dar foco a um campo faz o navegador rolar a coluna para deixá-lo à vista, e
  // esse ajuste acontece mesmo com `preventScroll` (a seleção do cursor também
  // o dispara). Rolando antes, o ajuste do foco desfazia o trabalho: o card
  // voltava para perto de onde estava. Só o corpo parecia funcionar, porque o
  // cursor dele fica no fim do formulário e o ajuste ia no MESMO sentido —
  // título, tag e fonte ficam no topo, e o ajuste ia no sentido contrário.
  //
  // O rAF espera o ajuste do foco terminar, para a conta da distância partir da
  // posição real e não de uma que já mudou.
  requestAnimationFrame(() => scrollEditingCardToTop(note.noteId));
}

// Foco no FIM do conteúdo, e com o fim à vista.
//
// Focar um campo não move o cursor para o fim nem rola o que já está escrito:
// numa fonte mais longa que o input, o campo abria mostrando o começo do texto,
// com o cursor num lugar que o usuário não via. Quem vai editar o que escreveu
// quer o fim da frase, que é onde se continua a escrever.
//
// `preventScroll` porque focar faz o navegador rolar o CONTÊINER até o campo, e
// essa rolagem desfaria a que acabou de pôr o card no topo. Ela não impede a
// rolagem DENTRO do campo, feita logo abaixo à mão.
function focusFieldEnd(el) {
  if (!el?.focus) return;

  el.focus({ preventScroll: true });

  const end = el.value?.length ?? 0;
  el.setSelectionRange?.(end, end);

  // Um input de uma linha rola na horizontal; a textarea, na vertical. Mandar
  // os dois para o fim resolve os dois casos sem precisar saber qual é qual.
  el.scrollLeft = el.scrollWidth;
  el.scrollTop = el.scrollHeight;
}

// O card em edição sobe para o topo da coluna: ele acabou de virar formulário e
// ficou bem mais alto que era, então de onde estava boa parte dele nasceria
// fora da vista — inclusive os botões de salvar.
//
// `scrollBy` no elemento, e não `scrollIntoView`: este rola o ancestral rolável
// mais próximo, que numa tela estreita pode ser a página, e a leitura sairia do
// lugar. É a mesma conta que a precedência do mouse já usa nesta lista.
function scrollEditingCardToTop(noteId) {
  const scroller = notesListEl.value;
  const card = scroller?.querySelector(`[data-note-id="${noteId}"]`);
  if (!scroller || !card) return;

  const delta =
    card.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
  if (Math.abs(delta) < 2) return;

  scroller.scrollBy({ top: delta, behavior: "smooth" });
}

// Alt+Enter no corpo: a quebra que o Enter deixou de fazer.
//
// Escrita à mão porque o navegador NÃO quebra linha em Alt+Enter — a tecla
// morre ali —, e porque o `preventDefault` do atalho tiraria a quebra nativa de
// qualquer forma. `setRangeText` mantém a pilha de desfazer da textarea, ao
// contrário de reescrever o valor inteiro.
function insertBodyLineBreak(event) {
  const el = event.target;
  if (!el || !editDraft.value) return;

  const body = editDraft.value.body ?? "";
  const start = el.selectionStart ?? body.length;
  const end = el.selectionEnd ?? start;

  // O `maxlength` do HTML só vale para o que é digitado: inserção por script
  // passa por cima dele, então o limite é conferido aqui.
  const max = el.maxLength > 0 ? el.maxLength : Infinity;
  if (body.length - (end - start) + 1 > max) return;

  el.setRangeText("\n", start, end, "end");
  editDraft.value.body = el.value;

  // A textarea rola sozinha atrás do cursor quando ele se move por digitação,
  // mas não quando a quebra vem daqui — e escrever no fim é o caso comum.
  if (el.selectionStart === el.value.length) el.scrollTop = el.scrollHeight;
}

// Desistir de verdade: o rascunho vai embora junto.
function cancelInlineEdit() {
  if (editingNoteId.value) delete pendingDrafts[editingNoteId.value];
  closeInlineEdit();
}

// Sair sem desistir: guarda o que está escrito e devolve o card à leitura.
function closeInlineEditKeepingDraft() {
  if (!editingNoteId.value) return;
  pendingDrafts[editingNoteId.value] = editDraft.value;
  closeInlineEdit();
}

function closeInlineEdit() {
  editingNoteId.value = null;
  editDraft.value = null;
  editError.value = "";
}

// Clique fora do card em edição fecha-o guardando o rascunho.
//
// Na fase de CAPTURA, para rodar antes do clique chegar ao destino: assim, ao
// clicar no lápis de outro card, este fecha primeiro e o outro abre em seguida,
// na ordem certa.
function handleEditClickOutside(event) {
  if (!editingNoteId.value || isSavingEdit.value) return;

  // Cadastro de tag aberto: ele é teleportado para o <body>, logo TODO clique
  // nele é "fora do card". Fechar aqui derrubaria o formulário que o próprio
  // card acabou de abrir.
  if (showAddTagModal.value) return;

  const card = document.querySelector(
    `[data-note-id="${editingNoteId.value}"]`
  );
  if (card && card.contains(event.target)) return;

  closeInlineEditKeepingDraft();
}

onMounted(() =>
  document.addEventListener("click", handleEditClickOutside, true)
);
onBeforeUnmount(() =>
  document.removeEventListener("click", handleEditClickOutside, true)
);

async function saveInlineEdit() {
  if (isSavingEdit.value || !editDraft.value) return;

  const draft = editDraft.value;
  const title = (draft.title ?? "").trim();

  // Mesma exigência do backend (NoteUpdateWithPermissionsDto): título é
  // obrigatório. Barrar aqui evita um 400 que chegaria como erro genérico.
  if (!title) {
    editError.value = "O título é obrigatório.";
    await nextTick();
    focusFieldEnd(editRefs.title);
    return;
  }

  isSavingEdit.value = true;
  editError.value = "";

  try {
    await noteStore.updateNoteFields(editingNoteId.value, {
      title,
      body: (draft.body ?? "").trim(),
      source: (draft.source ?? "").trim(),
      tagIds: (draft.tags ?? [])
        .map((tag) => tag.value)
        .filter((id) => id != null),
    });

    cancelInlineEdit();
    emit("notify", {
      type: "success",
      message: "Nota atualizada com sucesso!",
    });
  } catch (error) {
    // O card permanece aberto com o que foi digitado: fechar aqui apagaria o
    // trabalho justamente quando ele não chegou ao servidor.
    editError.value =
      error?.response?.data?.message ||
      error?.message ||
      "Não foi possível salvar a nota.";
  } finally {
    isSavingEdit.value = false;
  }
}

const showDeleteNoteModal = ref(false);
const noteIdToDelete = ref(null);

const showNoteCardModal = ref(false);

// >>> FLUXO 2: seta highlightTextNoteId para TextWithNotes rolar e iluminar o trecho.
// Chamado diretamente pelo botão "Ver trecho referenciado" na ficha.
function viewExcerpt(noteId) {
  if (!noteId) return;
  noteStore.highlightTextNoteId = noteId;
}

// >>> FLUXO 1: observa highlightNoteId setado por TextDisplayer.
// Quando recebe um noteId, aguarda a renderização, rola até a ficha e ilumina.
watch(
  () => noteStore.highlightNoteId,
  async (noteId) => {
    if (!noteId) return;
    await nextTick();

    const cardEl = document.querySelector(`[data-note-id="${noteId}"]`);
    if (!cardEl) {
      noteStore.highlightNoteId = null;
      return;
    }

    cardEl.scrollIntoView({ behavior: "smooth", block: "center" });
    cardEl.classList.add("note-card--highlight-flash");
    setTimeout(() => {
      cardEl.classList.remove("note-card--highlight-flash");
      noteStore.highlightNoteId = null;
    }, 4000); // >>> AJUSTE: dobrado de 2000 para 4000ms
  }
);
// >>> Precedência do mouse: o ponteiro sobre um destaque no texto rola a coluna
// para pôr aquela ficha no topo. Não muda o conjunto nem a ordem — só por onde
// a lista começa.
const notesListEl = ref(null);

/**
 * Espaço vazio no fim da lista, para a ÚLTIMA ficha também poder chegar ao topo.
 *
 * Sem ele, as últimas notas nunca alcançavam a primeira posição: rolar acaba
 * quando o fim do conteúdo encosta no fim da coluna, e a partir daí a ficha para
 * onde estiver. O defeito aparecia justamente onde você viu — nos últimos
 * destaques.
 *
 * A medida é a altura da coluna menos a da última ficha: é quanto falta para que
 * ela, sozinha, possa ocupar o topo. Qualquer ficha anterior precisa de menos,
 * então esta conta cobre todas.
 */
const tailSpace = ref(0);

async function measureTailSpace() {
  await nextTick();
  const scroller = notesListEl.value;
  if (!scroller) return;

  const cards = scroller.querySelectorAll("[data-note-id]");
  const last = cards[cards.length - 1];
  if (!last) {
    tailSpace.value = 0;
    return;
  }

  tailSpace.value = Math.max(
    0,
    Math.round(scroller.clientHeight - last.getBoundingClientRect().height)
  );
}

// A conta depende do conjunto exibido e da altura da coluna — as duas coisas que
// mudam por fora deste componente.
watch(() => noteStore.displayedNotes, measureTailSpace, { immediate: true });
onMounted(() => window.addEventListener("resize", measureTailSpace));
onBeforeUnmount(() => window.removeEventListener("resize", measureTailSpace));

watch(
  () => noteStore.hoveredNoteId,
  async (noteId) => {
    if (!noteId) return;
    // Card em edição congela este rastreamento: o ponteiro que passa por um
    // destaque no texto rolaria a coluna e levaria embora o campo em que se
    // está digitando. A precedência do mouse vale para ler, não para escrever.
    if (editingNoteId.value) return;
    await nextTick();

    const scroller = notesListEl.value;
    const card = scroller?.querySelector(`[data-note-id="${noteId}"]`);
    // A nota pode não estar na lista — recorte por clique, ou página que não a
    // contém. Sem ficha não há o que rolar, e não fazer nada é a resposta certa.
    if (!scroller || !card) return;

    // `scrollBy` no elemento, e não `scrollIntoView`: este rola o ANCESTRAL
    // rolável mais próximo, que numa tela estreita pode ser a página — e a
    // leitura sairia do lugar por causa de um gesto na coluna de notas.
    const delta =
      card.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    if (Math.abs(delta) < 2) return;

    scroller.scrollBy({ top: delta, behavior: "smooth" });
  }
);

const noteIdToView = ref(null);

const showUserCardModal = ref(false);
const selectedUserId = ref(null);

const showTagCardModal = ref(false);
const selectedTagId = ref(null);

function getCreatorAvatarUrl(note) {
  let path = note.createdByUserPhotoPath || note.createdByUser?.photoPath || "";
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  let cleanPath = path;
  if (!cleanPath.startsWith("/")) cleanPath = `/${path}`;
  return `${base}${cleanPath}`;
}

function openNoteCardModal(note) {
  if (!note) return;
  noteIdToView.value = note.noteId;
  showNoteCardModal.value = true;
}

function handleNoteCardModalClose() {
  showNoteCardModal.value = false;
  noteIdToView.value = null;
}

function openDeleteNoteModal(noteId) {
  noteIdToDelete.value = noteId;
  showDeleteNoteModal.value = true;
}

function handleDeleteModalClose() {
  showDeleteNoteModal.value = false;
  noteIdToDelete.value = null;
}

// ✅ PATCH: repassa toast do DeleteNoteForm para o pai de NoteList.vue
function handleDeleteNoteNotify(payload) {
  emit("notify", payload);
}

function openUserCardModal(userId) {
  if (!userId) return;
  selectedUserId.value = userId;
  showUserCardModal.value = true;
}

function handleUserCardModalClose() {
  showUserCardModal.value = false;
  selectedUserId.value = null;
}

function openTagCardModal(note, index) {
  if (!note) return;
  let tagId = null;
  if (Array.isArray(note.tagIds)) tagId = note.tagIds[index] ?? null;
  if (!tagId && typeof note.tagId !== "undefined") tagId = note.tagId;
  if (!tagId) return;
  selectedTagId.value = tagId;
  showTagCardModal.value = true;
}

function handleTagCardModalClose() {
  showTagCardModal.value = false;
  selectedTagId.value = null;
}

function isSingleWord(text) {
  return (text ?? "").trim().split(/\s+/).length === 1;
}

function noteIsSingleWord(note) {
  // noteLength é o comprimento exato do trecho marcado no texto
  // Se não disponível, calcula a partir dos ranges
  const len =
    note.noteLength ?? (note.textRangeEnd ?? 0) - (note.textRangeStart ?? 0);
  if (len > 0) {
    // Trecho com espaço certamente é multipalavra
    // Para ter certeza, verifica o título (preenchido com o trecho selecionado)
    return isSingleWord(note.title ?? "");
  }
  return isSingleWord(note.title ?? "");
}

function getTagStyle(note, index) {
  const textColor = note.tagSpecificColors?.[index] || "#111827";
  const bgColor =
    note.tagSpecificBackgrounds?.[index] ?? "var(--bg-active-nav)";

  if (noteIsSingleWord(note)) {
    // Nota de palavra única: tag em negrito com cor da tag, sem background
    return {
      color: textColor,
      fontWeight: "600",
    };
  } else {
    // Nota de trecho multipalavra: tag apenas com background, texto herda cor e peso do body
    return {
      backgroundColor: bgColor,
    };
  }
}

const canSeeObservationHighlight = (note) => {
  return (
    props.userId === props.textCreatedByUserId ||
    props.userId === props.activityCreatedByUserId ||
    props.userId === note.createdByUserId
  );
};

// Certo/Errado são sinalizações explícitas e destacam mesmo sem texto na
// observação; o amarelo (neutro) só faz sentido havendo descrição preenchida.
const hasObservationText = (note) => !!(note.observation && note.observation.trim());

const hasZeroRange = (note) => {
  const start = note.textRangeStart ?? note.textRangeStar ?? null;
  const end = note.textRangeEnd ?? null;
  return start === 0 && end === 0;
};

function formatNoteDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return (
    d.toLocaleDateString("pt-BR") +
    " às " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
}

function hasTags(note) {
  return Array.isArray(note.tagNames) && note.tagNames.length > 0;
}

function hasSource(note) {
  return String(note.source ?? "").trim() !== "";
}

function hasBody(note) {
  return String(note.body ?? "").trim() !== "";
}

// >>> Links da nota: extração, previews (imagem, YouTube, documento, site) e
// anexos vivem em src/utils/noteLinks.js, compartilhado com NoteGeneralForm.vue.
// A verificação dos links roda em paralelo, fora do carregamento das notas: os
// cards aparecem na hora e só ganham o selo de "quebrado" quando o veredito chega.
const { linkStatus, ensureLinkPreviews } = useLinkPreviews();

watch(
  () => displayedNotes.value,
  (notes) => {
    const urls = (notes ?? []).flatMap((note) => [
      ...extractUrls(note.body),
      ...extractUrls(note.source),
      ...extractUrls(note.observation),
    ]);
    if (urls.length) ensureLinkPreviews(urls);
  },
  { immediate: true, deep: false }
);

function buildNoteBody(note) {
  // Colapsa 3+ quebras de linha consecutivas (mesmo com espaços entre elas)
  // em duas — várias linhas em branco viram uma só. 1 ou 2 quebras são mantidas.
  const raw = String(note.body ?? "")
    .replace(/[ \t]*\n(?:[ \t]*\n){2,}[ \t]*/g, "\n\n")
    .trim();
  if (!raw) return "—";
  return linkifyBody(raw, { getStatus: (url) => linkStatus[url] });
}

const showAttachmentModal = ref(false);
const attachmentList = ref([]);

function openAttachmentModal(note) {
  const list = getNoteAttachments(note);
  if (!list.length) return;
  attachmentList.value = list;
  showAttachmentModal.value = true;
}

function handleAttachmentModalClose() {
  showAttachmentModal.value = false;
  attachmentList.value = [];
}

// Gating do vínculo Membro-Titular: criar nota só quando permitido para esta
// atividade (grid + opção "somente conteúdo do titular").
const { canCreateNoteInActivity } = useMemberRestrictions();
const memberCanCreateNote = computed(() =>
  canCreateNoteInActivity(props.activityCreatedByUserId)
);

// Motivo exibido no tooltip das ações que o vínculo bloqueia.
const blockedByMembershipHint =
  "Você só pode anotar em atividades preparadas pelo titular ou pelo círculo dele.";

// >>> NOVO: igual à opção "Adicionar Nota Vinculada" do NoteActionsButton.vue —
// instrui o usuário a selecionar um trecho do texto (mostra toast informativo).
function requestAddLinkedNote() {
  emit("notify", {
    type: "info",
    message:
      "Selecione com o mouse uma palavra ou trecho no texto para criar uma nota vinculada.",
  });
}

// >>> NOVO: igual ao DetachedNoteButton.vue — abre o modal de nota avulsa.
function requestAddDetachedNote() {
  emit("add-detached");
}
</script>

<template>
  <!--
    A faixa fica FORA da área que rola.

    Ela era `position: sticky` dentro de `.notes-list`, e grudar no topo de quem
    rola significa, por definição, que o conteúdo passa por baixo — os cards
    desapareciam atrás dela. O respiro que existia antes da rolagem era só o
    `gap` do flex: posicionava o primeiro card e não impedia nada.

    Aqui a área de rolagem começa DEPOIS da faixa, então não há por onde passar.
    O limite é estrutural: não depende de fundo opaco nem de acertar a cor no
    modo escuro.
  -->
  <div class="notes-pane">
    <!-- Foco por trecho: a lista deixou de ser a página e virou o recorte do
         trecho clicado no texto. A faixa é a única pista disso, e carrega a
         saída — sem ela o usuário acharia que perdeu as demais notas. -->
    <div v-if="noteStore.isNoteFocusActive" class="note-focus-bar">
      <div class="note-focus-info">
        <i
          :class="[
 'note-focus-icon',
            isFollowingReading ? 'fas fa-book-reader' : 'fas fa-filter',
          ]"
        ></i>
        <span v-if="isFollowingReading && noteStore.focusedNotes.length === 0">
          Nenhuma nota neste trecho
        </span>
        <span v-else>
          Exibindo
          {{ noteStore.focusedNotes.length }}
          {{ noteStore.focusedNotes.length === 1 ? "nota" : "notas" }}
          {{ isFollowingReading ? "do trecho em leitura" : "do trecho selecionado" }}
        </span>
      </div>

      <button
        type="button"
        class="note-focus-clear"
        @click="clearNoteFocusOrFollow()"
      >
        Ver todas
      </button>
    </div>

    <!-- Só isto rola. -->
    <div ref="notesListEl" class="notes-list">
    <!-- ✅ PATCH: estado de carregamento -->
    <!--
      Durante um salvamento inline o spinner NÃO entra: gravar dispara um
      refetch da página de notas, e trocar a lista pelo "Carregando notas..."
      arrancaria da tela justamente o formulário que está sendo salvo — ele
      sumiria e voltaria esmaecido antes de virar card. Como o refetch só
      substitui `notes` no fim, manter a lista antiga não mostra nada errado:
      o card em edição segue esmaecido até os dados novos chegarem.
    -->
    <div v-if="props.isLoadingNotes && !isSavingEdit" class="empty-state">
      <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>

      <p>Carregando notas...</p>
    </div>

    <!-- ✅ PATCH: estado vazio real -->
    <div v-else-if="displayedNotes.length === 0" class="empty-state">
      <!-- Atividade sem nenhuma nota de ninguém: oferece ações de criação -->
      <template v-if="noteStore.allNotes.length === 0">
        <div class="empty-cards-wrap">
          <p class="empty-headline">
            Não há notas nesta atividade.
          </p>

          <div class="empty-cards">
            <!-- Bloqueado pelo vínculo: os cards continuam à vista, esmaecidos
                 e inertes, com o motivo no tooltip. -->
            <button
              type="button"
              class="empty-card"
              :class="{ 'empty-card--blocked': !memberCanCreateNote }"
              :disabled="!memberCanCreateNote"
              :title="memberCanCreateNote ? null : blockedByMembershipHint"
              @click="requestAddLinkedNote"
            >
              <i class="fas fa-link"></i>
              <span>Adicionar nota vinculada</span>
            </button>

            <button
              type="button"
              class="empty-card"
              :class="{ 'empty-card--blocked': !memberCanCreateNote }"
              :disabled="!memberCanCreateNote"
              :title="memberCanCreateNote ? null : blockedByMembershipHint"
              @click="requestAddDetachedNote"
            >
              <i class="fas fa-plus"></i>
              <span>Adicionar nota avulsa</span>
            </button>
          </div>
        </div>
      </template>

      <!-- Seguindo a leitura, lista vazia não é busca sem resultado: é um trecho
           do texto que não tem nota. Dizer "nenhuma nota encontrada" mandaria o
           usuário conferir filtros que não têm nada a ver. -->
      <template v-else-if="isFollowingReading">
        <i class="fas fa-book-reader empty-icon"></i>
        <p>Nenhuma nota no trecho em leitura.</p>
        <p class="empty-hint">Role o texto para ver as notas de outros trechos.</p>
      </template>

      <!-- Estado vazio genérico (demais casos) -->
      <template v-else>
        <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
        <p>Nenhuma nota encontrada.</p>
      </template>
    </div>

    <!-- ✅ PATCH: lista normal -->
    <template v-else>
      <div
        v-for="note in displayedNotes"
        :key="note.noteId"
        class="note-card"
        :data-note-id="note.noteId"
        :class="{
 'note-observation-yellow':
            canSeeObservationHighlight(note) &&
            hasObservationText(note) &&
            note.observationType === 'Observation' &&
            !note.isObservationRead,
          'note-observation-green':
            canSeeObservationHighlight(note) &&
            note.observationType === 'Correct' &&
            !note.isObservationRead,
          'note-observation-red':
            canSeeObservationHighlight(note) &&
            note.observationType === 'Incorrect' &&
            !note.isObservationRead,
          'note-observation-blue':
            canSeeObservationHighlight(note) &&
            note.isObservationRead &&
            (hasObservationText(note) ||
              note.observationType === 'Correct' ||
              note.observationType === 'Incorrect'),
          'note-no-range': hasZeroRange(note),
          'note-card--editing': isEditing(note),
        }"
      >
        <div class="note-card-header">
          <div
            class="author-info"
            @click.stop="openUserCardModal(note.createdByUserId)"
            style="cursor: pointer"
          >
            <div
              v-if="getCreatorAvatarUrl(note)"
              class="author-avatar"
              :style="{
                backgroundImage: `url('${getCreatorAvatarUrl(note)}')`,
              }"
            ></div>
            <div
              v-else
              class="author-avatar flex items-center justify-center bg-slate-200 text-slate-500"
            >
              <i class="fas fa-user text-xs"></i>
            </div>
            <div class="author-meta">
              <span class="author-name">
                {{ String(note.createdByUserName ?? "usuário").toLowerCase() }}
              </span>
              <span
                v-if="formatNoteDate(note.updatedAt)"
                class="note-date-header"
              >
                {{ formatNoteDate(note.updatedAt) }}
              </span>
            </div>
          </div>

          <div class="note-actions">
            <!-- >>> NOVO: botão de anexo (clip) — à esquerda da âncora.
                 Só aparece quando a nota tem link de imagem, documento ou site. -->
            <button
              v-if="hasAttachment(note)"
              type="button"
              class="btn-icon-sm btn-icon-attachment btn-icon-attachment--active tooltip-left-bottom"
              data-tooltip="Ver anexo"
              @click.stop="openAttachmentModal(note)"
            >
              <i class="fas fa-paperclip"></i>
            </button>
            <!-- >>> FLUXO 2: botão "Ver trecho referenciado" — desabilitado para notas avulsas -->
            <button
              type="button"
              class="btn-icon-sm btn-icon-excerpt"
              :class="{
 'tooltip-left-bottom': !hasZeroRange(note),
                'btn-icon-excerpt--disabled': hasZeroRange(note),
              }"
              :disabled="hasZeroRange(note)"
              :aria-disabled="hasZeroRange(note) ? 'true' : 'false'"
              :data-tooltip="
                hasZeroRange(note) ? null : 'Ver trecho referenciado'
              "
              @click.stop="!hasZeroRange(note) && viewExcerpt(note.noteId)"
            >
              <i class="fas fa-anchor"></i>
            </button>
            <!-- >>> NOVO: Botão de compartilhamento -->
            <button
              type="button"
              class="btn-icon-sm btn-icon-share tooltip-left-bottom"
              data-tooltip="Compartilhar nota"
              @click.stop="
                openSharePopover(note.noteId, note.shareToken ?? null)
              "
            >
              <i class="fas fa-share-alt"></i>
            </button>
            <!-- Botão: ver nota completa -->
            <button
              type="button"
              class="btn-icon-sm btn-icon-brand tooltip-left-bottom"
              data-tooltip="Ver nota completa"
              @click.stop="openNoteCardModal(note)"
            >
              <i class="fas fa-expand-alt"></i>
            </button>

            <NoteCrudDropdown
              v-if="
                isSysUser ||
                props.userId == note.createdByUserId ||
                props.userId == props.textCreatedByUserId ||
                props.userId == props.activityCreatedByUserId ||
                canEditNote(note)
              "
              :noteId="note.noteId"
              :noteCreatedByUserId="note.createdByUserId"
              :noteUpdatedByUserId="note.updatedByUserId"
              :textCreatedByUserId="props.textCreatedByUserId"
              :textUpdatedByUserId="props.textUpdatedByUserId"
              :userId="props.userId"
              :isSysUser="isSysUser"
              :noteObservation="note.observation"
              :noteObservationType="note.observationType"
              :noteIsObservationRead="note.isObservationRead"
              :activityCreatedByUserId="props.activityCreatedByUserId"
              :canEditNote="canEditNote(note)"
              @request-delete="openDeleteNoteModal"
              @click.stop
            >
              <button
                type="button"
                class="btn-icon-sm btn-icon-neutral tooltip-left-bottom"
                data-tooltip="Opções"
              >
                <i class="fas fa-ellipsis-h"></i>
              </button>
            </NoteCrudDropdown>

            <div
              v-if="canManageNote(note)"
              class="note-select-wrapper"
              @click.stop
            >
              <input
                type="checkbox"
                class="note-select-checkbox"
                :checked="noteStore.selectedNoteIds.includes(note.noteId)"
                @change="noteStore.toggleSelection(note.noteId)"
              />
            </div>
            <div v-else class="note-select-wrapper" style="width: 0.9rem"></div>
          </div>
        </div>

        <!--
          Card em edição: o formulário SUBSTITUI os campos lidos, no lugar deles.
          Todos os quatro campos aparecem, e não só o do lápis clicado — assim dá
          para preencher uma fonte ou uma tag que ainda não existem e que, por
          não existirem, não teriam onde ser clicadas.
        -->
        <form
          v-if="isEditing(note)"
          class="note-edit"
          @submit.prevent="saveInlineEdit"
          @keydown.esc="cancelInlineEdit"
          @click.stop
        >
          <label class="note-edit-label" :for="`note-title-${note.noteId}`">
            Título
          </label>
          <input
            :id="`note-title-${note.noteId}`"
            :ref="setEditRef('title')"
            v-model="editDraft.title"
            type="text"
            class="note-edit-input"
            maxlength="100"
            placeholder="Digite o título da nota"
            :disabled="isSavingEdit"
          />

          <label class="note-edit-label" :for="`note-body-${note.noteId}`">
            Corpo
          </label>
          <!-- Textarea simples: o corpo é gravado como texto puro, e o que se vê
               no card (links, miniaturas) é montado na exibição pelo
               linkifyBody. Editar aqui é editar o que está guardado. -->
          <!-- Enter GRAVA, como nos demais campos: a textarea está aqui por o
               corpo caber em várias linhas, não por a quebra ser o gesto
               principal. Quebrar linha é Alt+Enter (`.exact` deixa passar
               Shift+Enter e Ctrl+Enter, que a própria textarea já quebra). -->
          <textarea
            :id="`note-body-${note.noteId}`"
            :ref="setEditRef('body')"
            v-model="editDraft.body"
            class="note-edit-textarea"
            maxlength="3000"
            rows="6"
            placeholder="Digite o corpo da nota (Enter salva, Alt+Enter quebra a linha)"
            :disabled="isSavingEdit"
            @keydown.enter.exact.prevent="saveInlineEdit"
            @keydown.enter.alt.exact.prevent="insertBodyLineBreak"
          ></textarea>

          <label class="note-edit-label">Tags</label>
          <!-- O Enter para aqui: dentro do seletor ele ESCOLHE ou CRIA uma tag,
               e se subisse até o <form> gravaria a nota no mesmo gesto — o
               usuário marcaria uma tag e a nota fecharia salva. Nos campos de
               texto o Enter continua salvando, que é o esperado. -->
          <div @keydown.enter.stop>
            <!-- Sem `ref`: nada mais chama o seletor de fora dele. Ele era
                 usado para abrir a lista ao entrar na edição, e essa abertura
                 automática saiu. -->
            <Multiselect
              v-model="editDraft.tags"
              :options="allTags"
              :multiple="true"
              :taggable="true"
              :loading="isLoadingTags"
              :allow-empty="true"
              label="label"
              track-by="value"
              placeholder="Selecione ou crie uma tag"
              tag-placeholder="Tecle enter para criar esta tag"
              select-label="Tecle enter para selecionar"
              deselect-label="Tecle enter para remover"
              selected-label="Selecionado"
              :disabled="isSavingEdit"
              @tag="createTagInline"
            />
          </div>

          <label class="note-edit-label" :for="`note-source-${note.noteId}`">
            Fonte
          </label>
          <input
            :id="`note-source-${note.noteId}`"
            :ref="setEditRef('source')"
            v-model="editDraft.source"
            type="text"
            class="note-edit-input"
            maxlength="300"
            placeholder="Digite a fonte da nota"
            :disabled="isSavingEdit"
          />

          <p v-if="editError" class="note-edit-error" role="alert">
            <i class="fas fa-exclamation-circle"></i>
            <span>{{ editError }}</span>
          </p>

          <div class="note-edit-actions">
            <button
              type="button"
              class="note-edit-btn note-edit-btn--ghost"
              :disabled="isSavingEdit"
              @click="cancelInlineEdit"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="note-edit-btn note-edit-btn--primary"
              :disabled="isSavingEdit"
            >
              <i class="fas fa-save"></i>
              <span>{{ isSavingEdit ? "Salvando..." : "Salvar" }}</span>
            </button>
          </div>
        </form>

        <!--
          Card em leitura. O lápis fica DENTRO do campo, logo depois do
          conteúdo, e aparece quando o mouse está naquele campo — não no card.
          Assim o que se aponta é o que se vai editar. O clique nos campos
          continua fazendo o que fazia: a tag abre o card dela, o link no corpo
          abre o link. Quem edita é o lápis.

          Campo vazio DIZ que está vazio, em vez de sumir: "tag não informada" e
          "fonte não informada" são chips tracejadas, na mesma linguagem de
          lacuna da estante, e o corpo vazio é só a frase — chip ali competiria
          com o texto corrido que costuma ocupar aquele lugar. Sem o # e sem o
          elo de corrente: o ícone anuncia um campo preenchido, e não há campo.
          As chips também abrem a edição, que é onde a lacuna se resolve.

          ⚠️ Aqui é tudo `v-show`, e nunca `v-if`. A lista de notas é um v-for
          com chave, e o Vue remenda os filhos de cada card comparando posições.
          O que é visível muda com `canInlineEdit` e com a recarga da nota — com
          `v-if`, cada mudança dessas inseria e removia nós no meio do card, e o
          remendo estourava em "insertBefore: the node ... is not a child of
          this node". Com `v-show` a forma do card é sempre a mesma.
        -->
        <div v-else class="note-read">
          <h4 class="note-title note-field">
            {{ note.title
            }}<span
              v-show="hasZeroRange(note)"
              class="no-range-chip"
              title="Nota avulsa, sem vínculo com trechos do texto"
              >nota avulsa</span
            ><button
              v-show="canInlineEdit(note)"
              type="button"
              class="note-field-edit"
              title="Editar título"
              aria-label="Editar título"
              @click.stop="startInlineEdit(note, 'title')"
            >
              <i class="fas fa-pencil-alt"></i></button
            >
          </h4>

          <!--
            Sem espaço entre as tags: `white-space: pre-wrap` no .note-content
            preserva o que vier no HTML, e a indentação do template viraria
            espaço em branco de verdade dentro do corpo da nota.
          -->
          <p class="note-content note-field"><span
              v-show="hasBody(note)"
              v-html="buildNoteBody(note)"
            ></span><span
              v-show="!hasBody(note)"
              class="note-empty-text"
              >corpo não informado</span
            ><button
              v-show="canInlineEdit(note)"
              type="button"
              class="note-field-edit"
              title="Editar corpo"
              aria-label="Editar corpo"
              @click.stop="startInlineEdit(note, 'body')"
            ><i class="fas fa-pencil-alt"></i></button></p>

          <!--
            Linha das lacunas: as chips de "não informado" moram todas aqui,
            lado a lado. A fonte só ocupa o rodapé quando EXISTE — o rodapé é o
            lugar da procedência escrita, e não de um aviso de que ela falta.
          -->
          <div class="note-tags-line note-field">
            <i v-show="hasTags(note)" class="fas fa-hashtag italic"></i>
            <button
              v-for="(subtypeName, index) in note.tagNames"
              :key="`tag-${note.tagIds?.[index] ?? index}`"
              type="button"
              class="subcategory-tag tag-link"
              @click.stop="openTagCardModal(note, index)"
              :style="getTagStyle(note, index)"
            >
              {{ subtypeName }}
            </button>
            <button
              v-show="hasTags(note) && canInlineEdit(note)"
              type="button"
              class="note-field-edit"
              title="Editar tags"
              aria-label="Editar tags"
              @click.stop="startInlineEdit(note, 'tags')"
            >
              <i class="fas fa-pencil-alt"></i>
            </button>
            <!-- Desabilitadas, e não escondidas, para quem não pode editar: a
                 lacuna continua sendo informação sobre a nota; o que some é o
                 convite a resolvê-la. -->
            <button
              v-show="!hasTags(note)"
              type="button"
              class="note-empty-chip"
              :disabled="!canInlineEdit(note)"
              :title="canInlineEdit(note) ? 'Informar as tags' : null"
              @click.stop="startInlineEdit(note, 'tags')"
            >
              tag não informada
            </button>
            <!-- Quebra de linha de flexbox: item de largura inteira e altura
                 zero, que empurra o que vem depois para a linha seguinte.
                 Só entra quando HÁ tags e falta a fonte — é o caso em que a
                 chip de lacuna ficaria enfileirada com as tags de verdade e
                 passaria por mais uma delas. Sem tags, as duas lacunas dividem
                 a mesma linha, que é onde elas se leem como um par. -->
            <span
              v-show="hasTags(note) && !hasSource(note)"
              class="note-line-break"
              aria-hidden="true"
            ></span>
            <button
              v-show="!hasSource(note)"
              type="button"
              class="note-empty-chip"
              :disabled="!canInlineEdit(note)"
              :title="canInlineEdit(note) ? 'Informar a fonte' : null"
              @click.stop="startInlineEdit(note, 'source')"
            >
              fonte não informada
            </button>
          </div>

          <div v-show="hasSource(note)" class="card-footer">
            <div class="footer-bottom-row">
              <p class="note-source note-field">
                <i class="fas fa-link footer-icon"></i>
                <span
                  >{{ note.source
                  }}<button
                    v-show="canInlineEdit(note)"
                    type="button"
                    class="note-field-edit"
                    title="Editar fonte"
                    aria-label="Editar fonte"
                    @click.stop="startInlineEdit(note, 'source')"
                  >
                    <i class="fas fa-pencil-alt"></i></button
                ></span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <!--
        Espaço no fim, para a última ficha poder subir até o topo. Só aparece
        para quem rola até o fim da lista; é o preço de a última nota alcançar a
        primeira posição, e vem de `tailSpace`.
      -->
      <div
        v-if="tailSpace > 0"
        class="notes-tail-space"
        :style="{ height: `${tailSpace}px` }"
        aria-hidden="true"
      ></div>
    </template>
    </div>
  </div>

  <Teleport to="body">
    <UserCardModal
      v-if="showUserCardModal"
      :show="showUserCardModal"
      :userId="selectedUserId"
      @close="handleUserCardModalClose"
    />

    <NoteCardModal
      v-if="showNoteCardModal"
      :show="showNoteCardModal"
      :noteId="noteIdToView"
      :userId="props.userId"
      @close="handleNoteCardModalClose"
    />

    <TagCardModal
      v-if="showTagCardModal"
      :show="showTagCardModal"
      :tagId="selectedTagId"
      @close="handleTagCardModalClose"
    />

    <!-- Cadastro de tag chamado de dentro do seletor do card. Mesmo formulário
         que o resto do sistema usa; o que muda é só de onde ele é aberto. -->
    <div
      v-if="showAddTagModal"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30 p-4"
      @click.self="showAddTagModal = false"
    >
      <div class="w-full max-w-2xl" @click.stop>
        <AddTagForm
          :initialName="newTagName"
          @tag-added="handleTagCreated"
          @close="showAddTagModal = false"
        />
      </div>
    </div>

    <DeleteNoteForm
      v-if="showDeleteNoteModal"
      :noteId="noteIdToDelete"
      @close="handleDeleteModalClose"
      @notify="handleDeleteNoteNotify"
    />

    <!-- >>> NOVO: modal de anexo (imagem/documento) -->
    <NoteAttachmentModal
      v-if="showAttachmentModal"
      :show="showAttachmentModal"
      :attachments="attachmentList"
      @close="handleAttachmentModalClose"
    />
  </Teleport>
  <!-- >>> NOVO: popover de compartilhamento -->
  <ShareNotePopover
    v-if="isSharePopoverOpen"
    :show="isSharePopoverOpen"
    :noteTitle="sharingNoteTitle"
    :shareUrl="absoluteShareUrl"
    :isGenerating="isGenerating"
    :isCopied="isCopied"
    :isRevoking="isRevoking"
    :shareError="shareError"
    :noteId="shareNoteId"
    :isOwner="isShareOwner"
    @close="closeSharePopover"
    @generate="
      (id) => {
        console.log('[NoteList] generate recebido, id:', id);
        generateLink(id);
      }
    "
    @copy="copyLink"
    @share-whatsapp="shareWhatsApp"
    @share-email="shareEmail(sharingNoteTitle)"
    @share-native="shareNative(sharingNoteTitle)"
    @revoke="revokeLink"
  />
</template>

<style scoped>
/* ── todos os estilos originais mantidos ── */

/* Invólucro: recebe a altura da coluna e a reparte entre a faixa (que ocupa o
   que precisar) e a lista (que fica com o resto e rola).

   `min-height: 0` não é higiene: sem ele um item flexível se recusa a ficar
   menor que o conteúdo, a lista cresce além da coluna e quem passa a rolar é a
   página — a faixa sairia de vista junto. */
.notes-pane {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  /* O respiro entre a faixa e o primeiro card, agora inviolável: nada sobe por
     ele, porque a área de rolagem começa depois. */
  gap: 10px;
}

/* `flex-shrink: 0` é requisito: a lista é uma coluna flexível, e sem isto o
   espaço do fim seria o primeiro a ser comprimido — some justamente quando é
   preciso. */
.notes-tail-space {
  flex: 0 0 auto;
  pointer-events: none;
}

.notes-list {
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  flex: 1;
  /* >>> PATCH: padding aumentado para sombra dos cards não ser cortada pelo overflow-y */
  padding-right: 6px;
  padding-left: 6px;
  padding-top: 4px;
  padding-bottom: 4px;
  scrollbar-gutter: stable;
}
/* ── Faixa do foco por trecho ─────────────────────────────────────────────── */
/* Fica no topo da lista e não rola junto: é o aviso de que a lista está
   recortada, e some junto com o foco. */
/* Sem `sticky` e sem `z-index`: a faixa não está mais dentro do que rola, então
   não há o que grudar nem sob o que passar. Ela é simplesmente o primeiro
   elemento da coluna. */
.note-focus-bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 10px;
  background-color: #f1eeff;
  border: 1px solid var(--brand-light);
  font-size: 0.8rem;
  color: var(--text-dark);
}

.note-focus-info {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.note-focus-icon {
  font-size: 0.75rem;
  color: #6d5ce7;
  flex-shrink: 0;
}

.note-focus-clear {
  flex-shrink: 0;
  border: none;
  background-color: var(--brand-light);
  color: var(--text-dark);
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 10px;
  padding: 3px 0.65rem;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

.note-focus-clear:hover {
  background-color: var(--brand-light-hover);
}

.note-focus-clear:focus,
.note-focus-clear:focus-visible {
  outline: none;
}

.note-card {
  background-color: var(--surface);
  border-radius: 14px;
  padding: 10px;
  transition: background-color 0.3s ease, border 0.3s ease, box-shadow 0.3s ease,
    transform 0.18s ease;
  /* >>> PATCH: borda e sombra espelhadas do NoteGeneralForm.vue */
  border: 1px solid var(--border-color);
  border-left: 0px solid transparent;
  box-shadow: 0 2px 8px rgba(17, 24, 39, 0.04);
}

.note-card:hover {
  box-shadow: 0 2px 8px rgba(17, 24, 39, 0.1);
  transform: translateY(-2px);
  z-index: 1;
}
.note-card:has(.row-dropdown-menu) {
  z-index: 40;
  isolation: isolate;
}
.note-observation-blue {
  background-color: #e6f0ff;
  border-left: 4px solid var(--info);
  box-shadow: 0 3px 6px rgba(59, 130, 246, 0.08);
}
.note-observation-green {
  background-color: #e6ffed;
  border-left: 4px solid #38a169;
  box-shadow: 0 3px 6px rgba(56, 161, 105, 0.08);
}
.note-observation-yellow {
  background-color: var(--warning-bg);
  border-left: 4px solid var(--warning);
  box-shadow: 0 3px 6px rgba(245, 158, 11, 0.08);
}
.note-observation-red {
  background-color: #ffe8e8;
  border-left: 4px solid #e53e3e;
  box-shadow: 0 3px 6px rgba(229, 62, 62, 0.08);
}
.note-no-range {
  border-top: 2px dashed var(--brand);
}
.note-card-header {
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.author-info {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 8px;
  transition: background-color 0.2s, transform 0.05s ease;
  line-height: 1.1;
  font-size: 0.85rem;
}
.author-info:active {
  transform: translateY(1px);
}
.author-info:hover {
  background-color: var(--surface-hover);
}
.author-avatar {
  margin-left: -6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--border-color);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;
  overflow: hidden;
}
.author-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.15;
  gap: 2px;
}
.author-name {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.note-date-header {
  font-size: 0.68rem;
  color: var(--text-light);
  font-style: italic;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-weight: 400;
}
.btn-icon-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  font-size: 0.78rem;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  position: relative;
}

.btn-icon-sm:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-icon-sm:focus,
.btn-icon-sm:focus-visible {
  outline: none;
  box-shadow: none;
}
.btn-icon-brand {
  background-color: #eee8ff;
  color: #49309a;
}
.btn-icon-brand:hover {
  background-color: #e2d9ff;
}
.btn-icon-neutral {
  background-color: var(--neutral-100);
  color: var(--text-dark);
}
.btn-icon-neutral:hover {
  background-color: var(--border-color);
}
/* >>> NOVO: cor do botão share */
.btn-icon-share {
  background-color: var(--success-bg);
  color: var(--success-text);
}
.btn-icon-share:hover {
  background-color: var(--success-bg);
}

/* ── Edição no próprio card ──────────────────────────────────────────────────
   O lápis fica FORA do fluxo do campo: dentro dele, entraria na justificação do
   título e do corpo e mexeria na quebra das linhas — o texto mudaria de forma
   só por existir um botão que ninguém está vendo. Aqui ele flutua na margem
   direita e o campo continua exatamente como era. */
/* O lápis mora DENTRO do campo, logo depois do conteúdo: é assim que ele diz o
   que vai editar. Como está no fluxo do texto, some por `opacity` e não por
   `display` — escondido, ele continua ocupando o lugar, e a linha não se
   reescreve quando o mouse entra e sai. */
.note-field-edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-left: 6px;
  padding: 0;
  vertical-align: middle;
  border: none;
  border-radius: 6px;
  background: var(--surface-2);
  color: var(--text-subtle);
  font-size: 0.62rem;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}

/* Aparece com o mouse NAQUELE campo, e não no card: apontar um campo e ver o
   lápis de todos não diria qual deles o clique abre. */
.note-field:hover .note-field-edit,
.note-field-edit:focus-visible {
  opacity: 1;
}

/* Teclado não tem hover: sem a regra acima, quem navega por Tab focaria um
   botão invisível. */
.note-field-edit:focus-visible {
  outline: 2px solid #c7d2fe;
  outline-offset: 1px;
}

.note-field-edit:hover {
  background: var(--brand-tint);
  color: #4f46e5;
}

/* Ausência de tag e de fonte: chip tracejada, sem preenchimento — a mesma
   linguagem de lacuna das chips do card da estante. Não é convite que aparece
   no hover: é INFORMAÇÃO sobre a nota, e por isso fica sempre visível, para
   quem pode editar e para quem não pode. O que o clique acrescenta, para quem
   pode, é o caminho de resolver a lacuna. */
.note-empty-chip {
  display: inline-flex;
  align-items: center;
  /* Mesmo respiro das chips de tag de verdade (.subcategory-tag): a linha tem
     `gap: 0`, e quem separa é a margem de cada chip. Sem ela, "tag não
     informada" e "fonte não informada" saíam encostadas uma na outra. */
  margin-left: 0.35rem;
  margin-bottom: 0.35rem;
  padding: var(--chip-padding);
  border: 1px dashed var(--neutral-400);
  border-radius: 9999px;
  background: transparent;
  color: var(--text-subtle);
  font-size: var(--chip-font-size);
  font-style: italic;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

/* Sem permissão de edição a chip continua dita, mas não se oferece: sem mão, e
   fora da ordem de tabulação (o próprio `disabled` cuida disso). */
.note-empty-chip:disabled {
  cursor: default;
}

.note-empty-chip:not(:disabled):hover {
  border-color: #a5b4fc;
  color: #4f46e5;
}

.note-empty-chip:focus-visible {
  outline: 2px solid #c7d2fe;
  outline-offset: 1px;
}

/* Quebra de linha dentro da fileira de chips: ocupa a largura toda e nada de
   altura, então o item seguinte não cabe ao lado e desce. */
.note-line-break {
  flex-basis: 100%;
  height: 0;
}

/* Corpo vazio: só a frase, sem chip. Ali costuma ficar texto corrido, e uma
   pílula no lugar dele competiria com o que os cards vizinhos mostram —
   o vazio deve parecer texto que falta, não um rótulo a mais. */
.note-empty-text {
  color: var(--text-subtle);
  font-style: italic;
}

/* O card em edição NÃO muda de cor: o formulário no lugar do texto já é o sinal
   mais forte que existe, e qualquer tinta a mais teria de disputar com os quatro
   fundos de observação logo acima. A classe `.note-card--editing` continua
   existindo por causa da regra de hover abaixo.

   Em edição, o card não levanta com o mouse: o gesto de levantar convida ao
   clique, e um card que já é formulário não está esperando ser aberto — está
   sendo usado. Mexer-se enquanto se digita nele também é ruído. */
.note-card--editing:hover {
  transform: none;
}

/* ── Formulário no card ──────────────────────────────────────────────────── */
.note-edit {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.note-edit-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-subtle);
  margin-top: 4px;
}

.note-edit-input,
.note-edit-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  background: var(--surface);
  font-family: inherit;
  font-size: 0.82rem;
  color: var(--text-dark);
}

.note-edit-textarea {
  resize: vertical;
  line-height: 1.45;
}

.note-edit-input:focus,
.note-edit-textarea:focus {
  outline: none;
  border-color: #a5b4fc;
  box-shadow: 0 0 0 3px rgba(199, 210, 254, 0.45);
}

.note-edit-input:disabled,
.note-edit-textarea:disabled {
  background: var(--surface-2);
  color: var(--text-subtle);
}

.note-edit-error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 0 0 0;
  font-size: 0.74rem;
  color: var(--danger-text);
}

.note-edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

.note-edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.note-edit-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.note-edit-btn--ghost {
  background: var(--surface);
  border-color: var(--neutral-200);
  color: var(--text-muted);
}

.note-edit-btn--ghost:hover:not(:disabled) {
  background: var(--surface-2);
}

/* Mesmo par do botão de criar do AddTextForm.vue: roxo claro com letra quase
   preta. Salvar é salvar — o botão que grava uma nota e o que grava um texto
   não podem ser de cores diferentes só por estarem em telas diferentes. */
.note-edit-btn--primary {
  background: var(--brand);
  color: var(--text-on-brand);
}

.note-edit-btn--primary:hover:not(:disabled) {
  background: var(--brand-hover);
}

/* O Multiselect traz o próprio CSS, num corpo maior que o do card. Alinha com
   os outros campos do formulário. */
.note-edit :deep(.multiselect) {
  min-height: 34px;
  font-size: 0.82rem;
}

.note-edit :deep(.multiselect__tags) {
  min-height: 34px;
  padding: 5px 40px 0 8px;
  border-color: var(--neutral-200);
  border-radius: 8px;
}

.note-edit :deep(.multiselect__tag) {
  background: var(--brand-tint);
  color: #4338ca;
  margin-bottom: 4px;
}

.note-edit :deep(.multiselect__tag-icon:after) {
  color: var(--brand);
}

.note-edit :deep(.multiselect__tag-icon:hover) {
  background: #c7d2fe;
}

/* As cores das mensagens da lista ("tecle enter para selecionar/remover/criar")
   ficam por conta do próprio vue-multiselect: verde para o que adiciona, vermelho
   para o que remove. É o mesmo par que os seletores do AddTextForm.vue mostram,
   pelo mesmo motivo — eles também não sobrescrevem essas cores. Havia aqui um
   índigo que pintava as três de uma cor só, e com isso "remover" prometia o
   mesmo que "selecionar". */

.note-title {
  font-size: 0.98rem;
  font-weight: 750;
  margin: 0 0 6px 0;
  line-height: 1.25;
  text-align: justify;
  text-justify: inter-word;
  hyphens: auto;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.no-range-chip {
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 9999px;
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  background-color: var(--brand-soft);
  color: #3730a3;
  vertical-align: middle;
}
.note-content {
  font-size: 0.84rem;
  line-height: 1.45;
  color: var(--text-light);
  margin: 0 0 10px 0;
  text-align: justify;
  text-justify: inter-word;
  hyphens: auto;
  overflow-wrap: anywhere;
  word-break: break-word;
  /* Preserva quebras de linha do corpo da nota */
  white-space: pre-wrap;
}
/* >>> NOVO: linha de mídia — previews lado a lado (com wrap) */
.note-content :deep(.note-media-row) {
  /* inline-flex (não flex/block) para não gerar linha em branco extra somada
     ao \n preservado pelo white-space: pre-wrap — mantém o lado-a-lado + wrap. */
  display: inline-flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
  vertical-align: top;
  margin: 0;
}
/* Dentro da linha, o espaçamento fica por conta do gap */
.note-content :deep(.note-media-row .note-preview) {
  margin: 0;
}

/* >>> NOVO: preview unificado (imagem / documento / site) — miniatura + legenda */
.note-content :deep(.note-preview) {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 96px;
  vertical-align: top;
  margin: 2px 6px 2px 0;
  text-decoration: none;
  line-height: 1.2;
}
.note-content :deep(.note-preview-thumb) {
  width: 96px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background-color: var(--surface-2);
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.1);
  overflow: hidden;
  transition: transform 0.12s ease, box-shadow 0.15s ease;
}
.note-content :deep(.note-preview:hover .note-preview-thumb) {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(17, 24, 39, 0.16);
}
.note-content :deep(.note-preview-img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.note-content :deep(.note-preview-icon) {
  font-size: 2.1rem;
  line-height: 1;
}
.note-content :deep(.note-preview-favicon) {
  width: 40px;
  height: 40px;
  object-fit: contain;
}
/* Fallback (imagem/favicon que não carrega): ícone genérico centralizado */
.note-content :deep(.note-preview-fallback) {
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: var(--text-subtle);
}
.note-content :deep(.note-preview-caption) {
  max-width: 96px;
  font-size: 0.72rem;
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* >>> Selo do YouTube sobre a miniatura do vídeo */
.note-content :deep(.note-preview-thumb) {
  position: relative;
}
.note-content :deep(.note-preview-play) {
  position: absolute;
  bottom: 4px;
  right: 5px;
  font-size: 1.05rem;
  line-height: 1;
  color: #ff0000;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
}

/* >>> Link quebrado: moldura vermelha, ícone do tipo e selo de alerta.
   Imagem/vídeo caem aqui sozinhos (via onerror); site e documento dependem do
   veredito do backend (POST /LinkPreview). */
.note-content :deep(.note-preview-thumb--broken),
.note-content :deep(.note-preview-fallback--broken) {
  position: relative;
  border-color: var(--danger-bg);
  background-color: var(--danger-bg);
  color: var(--danger-hover);
}
.note-content :deep(.note-preview-thumb--broken .note-preview-icon) {
  color: var(--danger-hover);
  opacity: 0.55;
}
.note-content :deep(.note-preview-badge) {
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: var(--danger-hover);
  color: #fff;
  font-size: 0.55rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.note-content :deep(.note-preview-caption--broken),
.note-content :deep(.note-preview--broken .note-preview-caption) {
  color: var(--danger-text);
  text-decoration: line-through;
}
/* vídeo quebrado: sai o selo do YouTube (a moldura já está em vermelho) */
.note-content :deep(.note-preview--broken .note-preview-play) {
  display: none;
}
.note-tags-line {
  /* Medida da pílula num lugar só: as chips de tag e as de lacuna dividem esta
     linha e precisam ser a MESMA forma. Dois valores iguais mantidos à mão
     sempre divergem. */
  --chip-padding: 3px 10px;
  --chip-font-size: 0.76rem;

  display: flex;
  align-items: center;
  gap: 0px;
  font-size: 0.82rem;
  color: var(--text-light);
  margin-bottom: 10px;
  line-height: 1.2;
  flex-wrap: wrap;
}
.note-tags-line .fa-hashtag {
  position: relative;
  top: -2px;
  font-size: 0.75rem;
  opacity: 0.85;
}
/* Mesma pílula da chip de lacuna (.note-empty-chip): mesmo raio, mesmo padding,
   mesmo corpo de fonte — as duas dividem a mesma linha, e formas diferentes
   fariam parecer que são coisas de naturezas diferentes. O que distingue tag
   preenchida de lacuna é a cor e o tracejado, não o formato.

   Virou `inline-flex` para isso: só assim a altura acompanha a da outra chip.
   O efeito colateral é no nome longo — antes ele se partia em dois pedaços de
   pílula (era o que o `box-decoration-break: clone` fazia), e agora quebra
   DENTRO da pílula, que cresce em altura. É o comportamento mais próximo do
   que se espera de uma etiqueta. */
.subcategory-tag {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  padding: var(--chip-padding);
  border-radius: 9999px;
  font-size: var(--chip-font-size);
  font-weight: 500;
  margin-left: 0.35rem;
  margin-bottom: 0.35rem;
}
.tag-link {
  cursor: pointer;
  /* Transparente, e não `none`: a chip de lacuna tem 1px de tracejado, e uma
     borda ausente aqui deixaria as duas com 2px de diferença de altura na mesma
     linha. Invisível, mas ocupando o mesmo lugar. */
  border: 1px solid transparent;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-weight: inherit;
}
.tag-link:hover {
  filter: brightness(0.95);
}
.card-footer {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px dashed var(--border-color);
}
.footer-bottom-row {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
}
.note-source {
  font-size: 0.74rem;
  color: var(--text-light);
  margin: 2px 0 0 0;
  line-height: 1.25;
  font-style: italic;
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr);
  column-gap: 6px;
  align-items: start;
  width: 100%;
  min-width: 0;
  text-align: left;
}

.note-source > span {
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-align: left;
}
.footer-icon {
  font-size: 0.65rem;
  opacity: 0.75;
  line-height: 1.2;
  margin-top: 2px;
}

/* >>> NOVO: botão de anexo (clip) — só aparece quando há link de imagem,
   documento ou site; sempre iluminado de amarelo. */
.btn-icon-attachment--active {
  background-color: var(--warning-bg);
  color: var(--warning);
  box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.35);
}

.btn-icon-attachment--active:hover {
  background-color: var(--warning-bg);
  color: var(--warning);
}
.note-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.note-select-wrapper {
  display: flex;
  align-items: center;
}
.note-select-checkbox {
  width: 0.9rem;
  height: 0.9rem;
  border: 1px solid var(--text-subtle);
  border-radius: 3px;
  background-color: var(--surface);
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  position: relative;
  margin: 0;
  flex-shrink: 0;
  transition: all 0.15s ease-in-out;
}
.note-select-checkbox:hover {
  border-color: var(--brand-light);
}
.note-select-checkbox:checked {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
}
.note-select-checkbox:checked::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 2px;
  width: 4px;
  height: 7px;
  border: solid #000;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.tooltip-left-bottom {
  position: relative;
}
.tooltip-left-bottom::after {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: var(--tooltip-bg);
  color: var(--tooltip-text);
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1;
  padding: 5px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transform: translateY(-2px);
  transition: opacity 0.15s ease, transform 0.15s ease;
  pointer-events: none;
  z-index: 80;
}
.tooltip-left-bottom:not(:disabled):not(
    .btn-icon-excerpt--disabled
  ):hover::after {
  opacity: 1;
  transform: translateY(0);
  transition-delay: 1s;
}

/* >>> FLUXO 2: botão "Ver trecho referenciado" */
/* >>> FLUXO 2: botão "Ver trecho referenciado" */
.btn-icon-excerpt {
  background-color: var(--info-bg);
  color: var(--link-hover);
}

.btn-icon-excerpt:hover:not(:disabled):not(.btn-icon-excerpt--disabled) {
  background-color: var(--info-border);
}

.btn-icon-excerpt--disabled,
.btn-icon-excerpt--disabled:hover,
.btn-icon-excerpt--disabled:active,
.btn-icon-excerpt:disabled,
.btn-icon-excerpt:disabled:hover {
  background-color: var(--neutral-100);
  color: var(--text-subtle);
  cursor: not-allowed !important;
  transform: none;
  filter: none;
  opacity: 1;
}

.btn-icon-excerpt--disabled *,
.btn-icon-excerpt--disabled:hover *,
.btn-icon-excerpt:disabled *,
.btn-icon-excerpt:disabled:hover * {
  cursor: not-allowed !important;
}

.btn-icon-excerpt--disabled i,
.btn-icon-excerpt--disabled:hover i,
.btn-icon-excerpt:disabled i,
.btn-icon-excerpt:disabled:hover i {
  color: var(--text-subtle);
  opacity: 0.75;
  pointer-events: none;
}

.btn-icon-excerpt--disabled::after,
.btn-icon-excerpt--disabled:hover::after,
.btn-icon-excerpt:disabled::after,
.btn-icon-excerpt:disabled:hover::after {
  display: none;
  opacity: 0;
}

/* >>> FLUXO 1: iluminação temporária da ficha — duração dobrada para 4s */
.note-card--highlight-flash {
  animation: card-flash 4s ease-out forwards;
}

@keyframes card-flash {
  0% {
    box-shadow: 0 0 0 3px var(--warning), 0 2px 8px rgba(17, 24, 39, 0.1);
    background-color: var(--warning-bg);
  }
  60% {
    box-shadow: 0 0 0 3px var(--warning), 0 2px 8px rgba(17, 24, 39, 0.1);
    background-color: var(--warning-bg);
  }
  100% {
    box-shadow: 0 2px 8px rgba(17, 24, 39, 0.04);
    background-color: var(--surface);
  }
}

.empty-state {
  width: 100%;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background-color: transparent;
  color: var(--text-light);
  border: none;
  box-shadow: none;
  border-radius: 12px;
  text-align: center;
  padding: 2rem 1rem;
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
}

/* Segunda linha do vazio: é instrução, não diagnóstico — fica mais discreta que
   a frase de cima, para não competir com ela. */
.empty-state .empty-hint {
  font-size: 0.8rem;
  font-weight: 400;
  color: var(--text-subtle);
}

/* >>> NOVO: estado vazio "só minhas notas" — frase + 4 cards (grid 2x2) */
.empty-cards-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.empty-cards-wrap .empty-headline {
  margin: 0 0 32px;
  text-align: center;
}

.empty-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  width: 100%;
}

/* Bloqueado pelo vínculo: visível, esmaecido e sem resposta ao hover. */
.empty-card--blocked,
.empty-card--blocked:hover {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.empty-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 10px;
  min-height: 94px;
  border: 1px solid #ece9f7;
  border-radius: 12px;
  background-color: var(--surface);
  color: #4b3f86;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.04);
  transition: border-color 0.15s ease, background-color 0.15s ease,
    box-shadow 0.15s ease, transform 0.12s ease;
}

.empty-card i {
  font-size: 1.1rem;
  color: #a99fd6;
  transition: color 0.15s ease;
}

.empty-card:hover {
  border-color: #d6cdf2;
  background-color: #faf8ff;
  box-shadow: 0 4px 12px rgba(75, 63, 134, 0.1);
  transform: translateY(-2px);
}

.empty-card:hover i {
  color: #6b5acd;
}

.empty-card:active {
  transform: translateY(0);
}

</style>